import React, { useState } from 'react';
import { Box, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

export type ProgressionLocaleCode = 'en' | 'fr';

export type ProgressionLocaleContent = {
  name: string;
  description: string;
};

export type ProgressionLocalesForm = {
  en: ProgressionLocaleContent;
  fr: ProgressionLocaleContent;
};

export function emptyProgressionLocales(): ProgressionLocalesForm {
  return {
    en: { name: '', description: '' },
    fr: { name: '', description: '' },
  };
}

export function progressionLocalesFromEntity(entity?: {
  name?: string;
  description?: string;
  locales?: Partial<ProgressionLocalesForm>;
}): ProgressionLocalesForm {
  return {
    en: {
      name: entity?.locales?.en?.name || entity?.name || '',
      description: entity?.locales?.en?.description || entity?.description || '',
    },
    fr: {
      name: entity?.locales?.fr?.name || '',
      description: entity?.locales?.fr?.description || '',
    },
  };
}

export function progressionPayloadFromLocales(locales: ProgressionLocalesForm) {
  return {
    locales,
    name: locales.en.name.trim(),
    description: locales.en.description,
  };
}

export function getProgressionDisplayName(
  entity: { name?: string; locales?: Partial<ProgressionLocalesForm> },
  locale: ProgressionLocaleCode = 'en',
): string {
  return entity.locales?.[locale]?.name || entity.locales?.en?.name || entity.name || '';
}

type ProgressionLocaleFieldsProps = {
  locales: ProgressionLocalesForm;
  onChange: (locales: ProgressionLocalesForm) => void;
  nameLabel?: string;
  descriptionLabel?: string;
};

export const ProgressionLocaleFields: React.FC<ProgressionLocaleFieldsProps> = ({
  locales,
  onChange,
  nameLabel,
  descriptionLabel,
}) => {
  const { t } = useTranslation();
  const [localeTab, setLocaleTab] = useState<ProgressionLocaleCode>('en');

  const updateField = (locale: ProgressionLocaleCode, field: keyof ProgressionLocaleContent, value: string) => {
    onChange({
      ...locales,
      [locale]: {
        ...locales[locale],
        [field]: value,
      },
    });
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t('progression.fields.localizedContent')}
      </Typography>
      <Tabs value={localeTab} onChange={(_, value) => setLocaleTab(value)} sx={{ mb: 2 }}>
        <Tab label={t('progression.localeTabs.en')} value="en" />
        <Tab label={t('progression.localeTabs.fr')} value="fr" />
      </Tabs>
      <Box display="flex" flexDirection="column" gap={2}>
        <TextField
          label={nameLabel || t('progression.fields.name')}
          required={localeTab === 'en'}
          value={locales[localeTab].name}
          onChange={(e) => updateField(localeTab, 'name', e.target.value)}
          fullWidth
        />
        <TextField
          label={descriptionLabel || t('progression.fields.description')}
          value={locales[localeTab].description}
          onChange={(e) => updateField(localeTab, 'description', e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
      </Box>
    </Box>
  );
};
